import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'

export interface CustomsCase {
  id: string
  cfss_number: string | null
  c18_number: string | null
  importer_name: string | null
  importer_eori: string | null
  agent_name: string | null
  initial_letter_date: string | null
  initial_deadline: string | null
  c18_letter_date: string | null
  c18_deadline: string | null
  c18_amount: number | null
  job_refs: string | null
  status: string
  appeal_reference: string | null
  appeal_date: string | null
  handed_to_agent_date: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface CaseNote {
  id: string
  case_id: string
  note_text: string
  action_type: string | null
  created_by: string | null
  created_at: string
}

export interface CaseStats {
  total: number
  open: number
  awaiting_response: number
  c18_received: number
  appealing: number
  paid: number
  closed: number
  total_c18_amount: number
  overdue_count: number
}

const isOverdue = (c: CustomsCase): boolean => {
  if (['paid', 'closed'].includes(c.status)) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  if (c.initial_deadline && new Date(c.initial_deadline) < today) return true
  if (c.c18_deadline && new Date(c.c18_deadline) < today) return true
  return false
}

export function useCustomsCases(options?: {
  statusFilter?: string
  searchQuery?: string
}) {
  const [cases, setCases] = useState<CustomsCase[]>([])
  const [stats, setStats] = useState<CaseStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { statusFilter, searchQuery } = options || {}

  const loadCases = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from('customs_cases')
        .select('*')
        .order('initial_deadline', { ascending: true, nullsFirst: false })

      if (searchQuery?.trim()) {
        const q = searchQuery.trim()
        query = query.or(
          `cfss_number.ilike.%${q}%,c18_number.ilike.%${q}%,importer_name.ilike.%${q}%,importer_eori.ilike.%${q}%`
        )
      }

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      const { data, error: qErr } = await query
      if (qErr) throw qErr

      const all = (data || []) as CustomsCase[]
      setCases(all)

      const overdue_count = all.filter(isOverdue).length
      const total_c18 = all.reduce((sum, c) => sum + (c.c18_amount || 0), 0)

      setStats({
        total: all.length,
        open: all.filter((c) => c.status === 'open').length,
        awaiting_response: all.filter((c) => c.status === 'awaiting_response').length,
        c18_received: all.filter((c) => c.status === 'c18_received').length,
        appealing: all.filter((c) => c.status === 'appealing').length,
        paid: all.filter((c) => c.status === 'paid').length,
        closed: all.filter((c) => c.status === 'closed').length,
        total_c18_amount: total_c18,
        overdue_count,
      })
    } catch (e: any) {
      setError(e.message || 'Failed to load cases')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, searchQuery])

  useEffect(() => {
    loadCases()
  }, [loadCases])

  const createCase = useCallback(
    async (values: Partial<CustomsCase>) => {
      const { data, error } = await supabase
        .from('customs_cases')
        .insert([values])
        .select()
        .single()
      if (error) throw error
      await loadCases()
      return data as CustomsCase
    },
    [loadCases]
  )

  const updateCase = useCallback(
    async (id: string, values: Partial<CustomsCase>) => {
      const { data, error } = await supabase
        .from('customs_cases')
        .update({ ...values, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      await loadCases()
      return data as CustomsCase
    },
    [loadCases]
  )

  const deleteCase = useCallback(
    async (id: string) => {
      const { error } = await supabase.from('customs_cases').delete().eq('id', id)
      if (error) throw error
      await loadCases()
    },
    [loadCases]
  )

  const fetchCase = useCallback(async (id: string) => {
    const { data, error } = await supabase
      .from('customs_cases')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data as CustomsCase
  }, [])

  const fetchNotes = useCallback(async (caseId: string) => {
    const { data, error } = await supabase
      .from('customs_case_notes')
      .select('*')
      .eq('case_id', caseId)
      .order('created_at', { ascending: false })
    if (error) throw error
    return (data || []) as CaseNote[]
  }, [])

  const addNote = useCallback(
    async (caseId: string, noteText: string, actionType: string | null, createdBy: string | null) => {
      const { data, error } = await supabase
        .from('customs_case_notes')
        .insert([
          {
            case_id: caseId,
            note_text: noteText,
            action_type: actionType,
            created_by: createdBy,
          },
        ])
        .select()
        .single()
      if (error) throw error
      return data as CaseNote
    },
    []
  )

  const lookupAgentByEori = useCallback(async (eori: string | null) => {
    if (!eori?.trim()) return null
    const { data, error } = await supabase
      .from('poas')
      .select('agent_name')
      .eq('eori_number', eori)
      .maybeSingle()
    if (error) throw error
    return data?.agent_name || null
  }, [])

  return {
    cases,
    stats,
    loading,
    error,
    reload: loadCases,
    createCase,
    updateCase,
    deleteCase,
    fetchCase,
    fetchNotes,
    addNote,
    lookupAgentByEori,
    isOverdue,
  }
}
